import React from "react";
import { addDays, format } from "date-fns";
import { useFormContext } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import { CHECKOUT_PAGES_ACTION_TYPES } from "repoV2/features/CheckoutPages/constants/CheckoutPagesReducer";
import { IVariant } from "repoV2/features/Listing/Listing.interfaces";
import { useCheckoutBasicDetailsContext } from "repoV2/features/CheckoutPages/modules/CheckoutBasicDetailsContextProvider";
import { booking_initial_user_id } from "repoV2/features/CheckoutPages/constants/CheckoutPages.constants";
import { IRepoV2ReduxState } from "repoV2/features/Common/modules/Reducer/Reducer.interface";
import { DAY_DATE_MONTH_FORMAT } from "repoV2/features/Common/modules/DateTime/modules/Time/Time.constants";
import { logError } from "repoV2/utils/common/error/error";
import { IExlySearchableDropdownOption } from "repoV2/features/Common/modules/Form/modules/ExlySearchableDropdown/ExlySearchableDropdown.interfaces";
import { callGetMerchandiseVariantsApi } from "repoV2/features/Listing/utils/callGetMerchandiseVariantsApi";
import {
    merchandise_shiprocket_error,
    merchandise_total_inventory,
    selected_merchandise_default_value,
    selected_merchandise_quantity_form_key,
    selected_merchandise_variant_form_key,
} from "../MerchandiseDetailsFormGroup.constants";
import useMerchandiseImages from "./useMerchandiseImages";
import { checkMerchandiseShipping } from "./checkMerchandiseShipping";
import { checkout_form } from "../../../../CheckoutForm/CheckoutForm.constants";
import { getUserFormFieldName } from "../../UserDetailsFormGroup/UserDetailsFormGroup.utils";

const {
    international_details: { field_names },
} = checkout_form;

const useMerchandiseDetailsFormGroup = () => {
    const { getValues, setValue, watch } = useFormContext();

    const [selectedVariants, setSelectedVariants] = React.useState<
        Record<string, string>
    >({});
    const [outOfStockVariants, setOutOfStockVariants] = React.useState<
        Record<string, boolean>
    >({});

    const {
        listingUuid,
        shippingDetails,
        isShiprocketEnabled,
        allowMerchandiseOverflow,
        purchasedMerchandiseInventory,
        totalMerchandiseInventory,
    } = useCheckoutBasicDetailsContext();

    const {
        order = [],
        variants = {},
        renderData = {},
    } = useSelector(
        (state: IRepoV2ReduxState) =>
            state.checkoutPages.listingDetails?.[listingUuid]
                ?.merchandiseVariantsDetails?.data
    ) || {};

    const isVariantMerchandise = Boolean(Object.keys(variants).length);

    const selectedMerchandiseVariant: IVariant =
        watch(selected_merchandise_variant_form_key) || {};
    const selectedQuantity: IExlySearchableDropdownOption = watch(
        selected_merchandise_quantity_form_key
    );

    const shiprocketData =
        useSelector(
            (state: IRepoV2ReduxState) =>
                state.checkoutPages.listingDetails?.[listingUuid]
                    ?.merchandiseShiprocketDetails?.data
        ) || {};

    const priceDetailsSection = useSelector(
        (state: IRepoV2ReduxState) =>
            state.checkoutPages.listingDetails[listingUuid]?.priceSection
    );

    const priceDetails = priceDetailsSection?.data || {};

    const { showInternationalInputFields: isInternationalEnabled = false } =
        priceDetails;

    const { variantImages } = useMerchandiseImages();

    // here, merchandise_total_inventory is the total inventory default value
    let variantQuantity = merchandise_total_inventory;

    // incase it is a variant type merchandise, then we checkt the values of the selected variant
    const inventory = isVariantMerchandise
        ? selectedMerchandiseVariant
        : {
              total_inventory: totalMerchandiseInventory,
              purchased_inventory: purchasedMerchandiseInventory,
          };

    // same with allow overflow, we see if it is a variant merchandise, and use its data
    const allowOverflow = isVariantMerchandise
        ? selectedMerchandiseVariant.allow_overflow
        : allowMerchandiseOverflow;

    // incase the allow overflow is true, then regardless of inventory availability, we
    // show the item in stock
    // else, we use the available inventory, which is (total - purchased inventory)
    variantQuantity =
        (allowOverflow
            ? merchandise_total_inventory
            : Math.abs(
                  inventory?.total_inventory - inventory?.purchased_inventory
              )) || merchandise_total_inventory;

    // check if user has selected a variant
    const isVariantSelected = Boolean(
        Object.keys(selectedMerchandiseVariant).length
    );

    // shipping days values to be used - be it international or a domestic case
    const shippingDays =
        (isInternationalEnabled
            ? shippingDetails?.shipping_days_international
            : shippingDetails?.shipping_days) || 0;

    // shipping date to be shown to user on UI
    const shippingDate = format(
        addDays(new Date(), shippingDays),
        DAY_DATE_MONTH_FORMAT
    );

    const dispatch = useDispatch();

    // This is the function we call whenever user selects a variant type
    const handleSelectedVariantChange = async ({
        key,
        value,
    }: {
        key: string;
        value: string;
    }) => {
        // we store the new selected variant in key value pairs, where the key is the
        // variant type, and the value is the variant value
        const newSelectedVariants = {
            ...selectedVariants,
            [key]: value,
        };

        setSelectedVariants({
            ...newSelectedVariants,
        });

        // order contains the all possible variant types, so incase the newSelectedVariants length is the
        // same as that of order, this means the user has selected all variant types, and a variant is selected here
        if (Object.keys(newSelectedVariants).length === order.length) {
            // Variant is of the format -
            // Color=red,Size=Merdium...
            // so we first make the key of the selected variant from newSelectedVariants
            const variantKey = order
                .map(
                    variantName =>
                        `${variantName}=${newSelectedVariants[variantName]}`
                )
                .join(",");
            if (variantKey in variants) {
                const newVariant = variants[variantKey];

                // we set the new selected variant in react hook form
                setValue(
                    selected_merchandise_variant_form_key,
                    variants[variantKey]
                );

                // Incase user has already verified his pincode, for shiprocket and the variant is changed now,
                // then we need to refetch the data, with the new variant this time, as each variant can have
                // different weight and dimensions, impacting the shipping charges by shiprocket
                if (shiprocketData.isVerified) {
                    const data = getValues() || {};

                    // we get the latest value of pincode from form values
                    const pinCodeValue =
                        data[
                            getUserFormFieldName(
                                booking_initial_user_id,
                                field_names.postal_code
                            )
                        ] || "";

                    if (pinCodeValue) {
                        const response = await checkMerchandiseShipping({
                            listingUuid,
                            currencyName: priceDetails.currency_name || "",
                            isInternationalEnabled:
                                priceDetails.showInternationalInputFields ||
                                false,
                            pinCode: pinCodeValue,
                            variantUuid: variants[variantKey]?.uuid || "",
                        });

                        if (response?.isError) {
                            dispatch({
                                type: CHECKOUT_PAGES_ACTION_TYPES.SET_MERCHANDISE_SHIPROCKET_DATA,
                                payload: {
                                    listingUuid,
                                    data: {
                                        ...shiprocketData,
                                        value: {},
                                        isVerified: true,
                                        error: merchandise_shiprocket_error,
                                    },
                                },
                            });
                        } else {
                            dispatch({
                                type: CHECKOUT_PAGES_ACTION_TYPES.SET_MERCHANDISE_SHIPROCKET_DATA,
                                payload: {
                                    listingUuid,
                                    data: {
                                        ...shiprocketData,
                                        value: {
                                            shippingCharge:
                                                response?.shipping_charge,
                                            shippingDays:
                                                response?.shipping_days,
                                        },
                                        isVerified: true,
                                        error: "",
                                    },
                                },
                            });
                        }
                    }
                }

                // Check the maximum available quantity for the new variant
                const newMaxAvailableQuantity =
                    (newVariant.allow_overflow
                        ? merchandise_total_inventory
                        : newVariant.total_inventory -
                          newVariant.purchased_inventory) ||
                    merchandise_total_inventory;

                // Incase the new variant max quantity is less than the already selected quantity,
                // then we reset the quantity dropdown value
                if (newMaxAvailableQuantity < +selectedQuantity.value) {
                    setValue(
                        selected_merchandise_quantity_form_key,
                        selected_merchandise_default_value
                    );
                }
            }
        }
    };

    const getMetchandiseVariants = async () => {
        try {
            // we call this api, again as on server side call we do not get the price details
            const response = await callGetMerchandiseVariantsApi({
                listingUuid,
                isCheckout: true,
            });

            const variantsData = response?.data?.data || {};
            dispatch({
                type: CHECKOUT_PAGES_ACTION_TYPES.SET_MERCHANDISE_VARIANTS_DATA,
                payload: {
                    isFetched: true,
                    listingUuid,
                    data: {
                        order: variantsData?.variant_type_order || [],
                        variants: variantsData?.variants || {},
                        renderData: variantsData?.variants_display_data || {},
                    },
                },
            });

            const variantsOptions = variantsData?.variants || {};
            const outOfStockVariantOptions: Record<string, boolean> = {};
            const inStockVariantOptions: string[] = [];

            // here we seperate the out of stock variants, and in stock variants
            Object.keys(variantsOptions).forEach(variant => {
                const variantValue: IVariant = variantsOptions[variant];
                const isVariantOutOfStock = variantValue.allow_overflow
                    ? false
                    : !(
                          variantValue.total_inventory >
                          variantValue.purchased_inventory
                      );
                if (isVariantOutOfStock) {
                    outOfStockVariantOptions[variant] = true;
                } else {
                    inStockVariantOptions.push(variant);
                }
            });
            setOutOfStockVariants({ ...outOfStockVariantOptions });

            // by default we need to set any in stock variant as default
            if (inStockVariantOptions.length) {
                setValue(
                    selected_merchandise_variant_form_key,
                    variantsOptions[inStockVariantOptions[0]]
                );
                const selectedVariantValue: Record<string, string> = {};
                // Variant is of the format -
                // Color=red,Size=Merdium...
                // So we split it first, to get the key value pairs
                inStockVariantOptions[0].split(",").forEach(keyValue => {
                    const [key, value] = keyValue.split("=");
                    selectedVariantValue[key] = value;
                });

                setSelectedVariants({ ...selectedVariantValue });
            }
        } catch (err) {
            logError({
                error: err,
                when: "api to get merchandise variants",
                occuredAt:
                    "repoV2/features/CheckoutPages/modules/PaymentDetailsSection/modules/FormGroups/modules/MerchandiseDetailsFormGroup/utils/MerchandiseDetailsFormGroup.utils.ts",
            });
        }
    };

    React.useEffect(() => {
        getMetchandiseVariants();
    }, []);

    return {
        order,
        variants,
        renderData,
        selectedVariants,
        handleSelectedVariantChange,
        selectedMerchandiseVariant,
        variantImages,
        variantQuantity,
        isVariantSelected,
        shippingDate,
        shippingDays,
        isShiprocketEnabled,
        isVariantMerchandise,
        outOfStockVariants,
    };
};

export default useMerchandiseDetailsFormGroup;
